/**
 * PDP purchase options — bundle cards that swap the product variant in place.
 *
 * Extracted verbatim from the `custom_liquid` block on the hair template; the
 * markup now lives in `snippets/pdp-purchase-options.liquid`. Behaviour is
 * unchanged: click a `.qty-pill[data-variant-id]`, and rather than navigating we
 * fetch that variant's rendered section (`?variant=...&section_id=...`) and swap
 * it in, so price, badges and buy buttons all update together.
 *
 * Bound by event delegation on document, so it survives the section being
 * replaced by its own swap. Contract with the Liquid: `.qty-pill` carries
 * `data-variant-id` and `data-qty`; pills without `data-variant-id` (the
 * subscription page's links) are left to navigate natively.
 */

(function () {
  // Keep aria-checked in step with the .active class — the cards are
  // role="radio", so the class alone would leave assistive tech out of date.
  function setChecked(el, on) {
    if (el.getAttribute('role') === 'radio') el.setAttribute('aria-checked', on ? 'true' : 'false');
  }

  var sectionCache = {};
  var currentRequest = 0; // Race condition guard
  
  // ── Find the product section + product URL relative to a given element ──
  function getContext(el) {
    var productInfo = el.closest('product-info');
    var sectionEl = null;
    var sectionId = null;
  
    if (productInfo) {
      sectionEl = productInfo.closest('.shopify-section, [id^="shopify-section-"]');
      if (sectionEl) {
        sectionId = sectionEl.id.replace('shopify-section-', '');
      } else {
        sectionEl = productInfo;
        sectionId = productInfo.dataset.originalSection || productInfo.dataset.section;
      }
    } else {
      sectionEl = el.closest('.shopify-section, [id^="shopify-section-"]');
      if (sectionEl) sectionId = sectionEl.id.replace('shopify-section-', '');
    }
  
    if (!sectionEl || !sectionId) return null;
  
    var productUrl = productInfo ? productInfo.dataset.url : null;
    if (!productUrl) {
      var productLink = sectionEl.querySelector('a[href*="/products/"]');
      if (productLink) productUrl = productLink.pathname;
    }
    if (!productUrl) productUrl = window.location.pathname;
  
    return { sectionEl: sectionEl, sectionId: sectionId, productUrl: productUrl };
  }
  
  // ── Fetch and cache a variant's section HTML ──
  function prefetchVariant(variantId, productUrl, sectionId) {
    var key = productUrl + ':' + variantId;
    if (sectionCache[key]) return;
    sectionCache[key] = fetch(productUrl + '?variant=' + variantId + '&section_id=' + sectionId)
      .then(function(r) { return r.text(); })
      .catch(function() { delete sectionCache[key]; return null; });
  }
  
  // ── Patch pill active state in parsed HTML ──
  function patchPillState(doc, variantId) {
    doc.querySelectorAll('.qty-pill').forEach(function(p) { p.classList.remove('active'); setChecked(p, false); });
    doc.querySelectorAll('[id^="savings-"]').forEach(function(s) { s.classList.add('hidden'); });
    var match = doc.querySelector('.qty-pill[data-variant-id="' + variantId + '"]');
    if (match) {
      match.classList.add('active');
      setChecked(match, true);
      var savings = doc.getElementById('savings-' + match.dataset.qty);
      if (savings && savings.textContent.trim() !== '') savings.classList.remove('hidden');
    }
  }
  
  // ── Sync pill UI on a given root element ──
  function syncPillsIn(root, variantId) {
    root.querySelectorAll('.qty-pill').forEach(function(p) { p.classList.remove('active'); setChecked(p, false); });
    root.querySelectorAll('[id^="savings-"]').forEach(function(s) { s.classList.add('hidden'); });
    var match = root.querySelector('.qty-pill[data-variant-id="' + variantId + '"]');
    if (match) {
      match.classList.add('active');
      setChecked(match, true);
      var savings = root.querySelector('#savings-' + match.dataset.qty);
      if (savings && savings.textContent.trim() !== '') savings.classList.remove('hidden');
    } else {
      var first = root.querySelector('.qty-pill[data-variant-id]');
      if (first) { first.classList.add('active'); setChecked(first, true); }
    }
  }
  
  // ── Switch variant (instant if prefetched) ──
  function setVariant(variantId, ctx) {
    if (window.location.pathname === ctx.productUrl || window.location.pathname.indexOf('/products/') === 0) {
      var url = new URL(window.location);
      url.searchParams.set('variant', variantId);
      history.replaceState(null, '', url);
    }
  
    var isModal = ctx.sectionEl.tagName === 'PRODUCT-INFO';
    var requestId = ++currentRequest;
  
    var key = ctx.productUrl + ':' + variantId;
    var htmlPromise = sectionCache[key]
      || fetch(ctx.productUrl + '?variant=' + variantId + '&section_id=' + ctx.sectionId).then(function(r) { return r.text(); });
  
    htmlPromise
      .then(function(html) {
        if (!html || requestId !== currentRequest) return; // Stale or failed — discard
        var doc = new DOMParser().parseFromString(html, 'text/html');
        patchPillState(doc, variantId);
  
        if (isModal) {
          var newProductInfo = doc.querySelector('product-info');
          if (newProductInfo) {
            ctx.sectionEl.innerHTML = newProductInfo.innerHTML;
          } else {
            var newSection = doc.querySelector('.shopify-section');
            ctx.sectionEl.innerHTML = newSection ? newSection.innerHTML : doc.body.innerHTML;
          }
        } else {
          ctx.sectionEl.innerHTML = doc.body.innerHTML;
        }
  
      })
      .catch(function() {
        if (requestId !== currentRequest) return;
        window.location.href = ctx.productUrl + '?variant=' + variantId;
      });
  }
  
  // ── Event delegation: one-time pills swap variants in-place ──
  // (Link-pills on the subscription page have no data-variant-id, so they navigate natively.)
  document.addEventListener('click', function(e) {
    var pill = e.target.closest('.qty-pill');
    if (!pill) return;
  
    var variantId = pill.dataset.variantId;
    if (!variantId) return; // link-pill → let the browser navigate
  
    var ctx = getContext(pill);
    if (!ctx) return;
  
    e.preventDefault();
  
    // Optimistic UI scoped to this pill's container
    var container = pill.closest('.product-options');
    if (!container) { var row = pill.closest('.quantity-row'); container = row ? row.parentElement : document; }
    syncPillsIn(container, variantId);
  
    setVariant(variantId, ctx);
  });
  
  // ── Init: pill state from ?variant= + prefetch (one-time page only) ──
  (function() {
    var pills = document.querySelectorAll('.qty-pill[data-variant-id]');
    if (!pills.length) return; // subscription page — pills are links, nothing to sync
  
    var activeVariant = new URLSearchParams(window.location.search).get('variant');
    syncPillsIn(document, activeVariant);
  
    // Prefetch — with Safari fallback for requestIdleCallback
    var scheduleIdle = window.requestIdleCallback || function(cb) { setTimeout(cb, 1); };
    scheduleIdle(function() {
      var ctx = getContext(pills[0]);
      if (ctx) {
        pills.forEach(function(pill) {
          prefetchVariant(pill.dataset.variantId, ctx.productUrl, ctx.sectionId);
        });
      }
    });
  })();
})();
